import React, { useState, useEffect } from 'react';
import axios from 'axios';

const IpChecker = ({ onClose }) => {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      // Since we don't have a backend anymore, we'll use a public IP API
      const response = await axios.get('https://api.ipify.org?format=json');
      const ipResponse = await axios.get(`https://ipapi.co/${response.data.ip}/json/`);
      
      const userData = {
        ip_address: response.data.ip,
        city: ipResponse.data.city,
        region: ipResponse.data.region,
        country: ipResponse.data.country_name,
        postal: ipResponse.data.postal,
        latitude: ipResponse.data.latitude,
        longitude: ipResponse.data.longitude,
        timezone: ipResponse.data.timezone,
        org: ipResponse.data.org,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString()
      };
      
      setUserInfo(userData);
    } catch (error) {
      setError('There was an error getting your IP information. Please try again later.');
      console.error('Error fetching IP info:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch(() => {
      alert('Failed to copy to clipboard');
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h4>Your IP Information</h4>
      
      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {userInfo && (
        <div className="mt-4">
          <div className="card">
            <div className="card-header">
              <h5>Network Information</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <p><strong>IP Address:</strong></p>
                  <div className="input-group mb-3">
                    <input 
                      type="text" 
                      className="form-control" 
                      value={userInfo.ip_address} 
                      readOnly 
                    />
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button"
                      onClick={() => copyToClipboard(userInfo.ip_address)}
                    >
                      Copy
                    </button>
                  </div>
                  
                  <p><strong>Organization:</strong> {userInfo.org || 'N/A'}</p>
                  <p><strong>Timezone:</strong> {userInfo.timezone || 'N/A'}</p>
                </div>
                
                <div className="col-md-6">
                  <p><strong>Location:</strong></p>
                  <p>{userInfo.city}, {userInfo.region}, {userInfo.country}</p>
                  <p><strong>Postal Code:</strong> {userInfo.postal || 'N/A'}</p>
                  <p><strong>Coordinates:</strong> {userInfo.latitude}, {userInfo.longitude}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card mt-3">
            <div className="card-header">
              <h5>Browser Information</h5>
            </div>
            <div className="card-body">
              <p><strong>User Agent:</strong></p>
              <div className="input-group">
                <textarea 
                  className="form-control" 
                  rows={3} 
                  value={userInfo.user_agent} 
                  readOnly 
                  style={{ fontSize: '12px' }}
                ></textarea>
                <button 
                  className="btn btn-outline-secondary" 
                  type="button"
                  onClick={() => copyToClipboard(userInfo.user_agent)}
                >
                  Copy
                </button>
              </div>
              <small className="text-muted">Timestamp: {new Date(userInfo.timestamp).toLocaleString()}</small>
            </div>
          </div>

          <div className="mt-3">
            <button className="btn btn-primary" onClick={fetchUserInfo}>
              Refresh Information
            </button>
          </div>
        </div>
      )}

      <div className="mt-4">
        <div className="alert alert-info">
          <h6>About this tool:</h6>
          <p>This tool shows your current IP address and location information. 
          This data is publicly available and is used by websites to provide location-based services.</p>
        </div>
      </div>
    </div>
  );
};

export default IpChecker;
